<template>
  <div class="etching-section">
    <div
      v-if="ctaText"
      class="flex-inline cta"
      role="button"
      aria-label="Open Customization Modal"
      @click="toggleEtchModal"
    >
      <CloudinaryImage
        :class="['preview-img']"
        :public-id="'h32otax4jeus7qbaolef'"
        :alt="'etching preview'"
        :options="{ width: 64, height: 60, quality: 'auto:eco' }"
      />
      <div class="flex-col">
        <div class="ff-text-500 etch-header" v-text="ctaText" />
        <div class="etch-desc cta flex-inline mgn-btm-pt25">
          <div>Only available at</div>
          <div class="header-logo" />
        </div>
      </div>
      <ArrowRightSLine class="right-arrow" />
    </div>

    <ModalWindow
      v-if="state.showEtchModal"
      :class="'modal-etch full-window ' + bannerCrop('top')"
      @closed="toggleEtchModal"
    >
      <div class="modal-body">
        <div class="desk-hor-mob-col modal-body-content">
          <div class="img-container">
            <!-- dynamic image transformation with text in front -->
            <div class="etch-img-overlay-container">
              <CloudinaryImage
                :public-id="cloudinaryId"
                :alt="productName"
                :options="imageOptions"
                :image-classes-override="['current-image']"
              />
              <div
                v-if="state.userInput"
                class="etch-img-overlay etch-img-overlay--svg text"
                :style="styleEtchingDimensions"
              >
                <SvgjsText
                  :base-font-size="state.baseFontSize"
                  :text="state.userInput"
                  :aspect-ratio="{ width: prodWidth, height: prodHeight }"
                  :svg-color="etchingColor"
                />
              </div>
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-if="state.etchingSvg"
                class="etch-img-overlay etch-img-overlay--svg img"
                :style="styleEtchingDimensions"
                v-html="state.etchingSvg"
              />
              <template v-if="state.etchPage > 0">
                <div class="etch-img-pattern" v-html="etchingSvgPattern" />
                <div class="etch-txt-pattern" v-html="textSvgPattern" />
              </template>
              <!-- eslint-enable vue/no-v-html -->
            </div>
          </div>

          <div class="customize-section flex-col">
            <div v-if="state.etchPage > 0" class="h5 pointer back uc flex-inline" @click="backButton()">
              <ArrowLeftLine />Back
            </div>
            <h3 class="mgn-top-1-pt5" :class="{ 'mgn-btm-1-pt5': !(state.etchPage > 0) }">
              Personalize Your {{ productName }}
            </h3>
            <p v-if="surcharge && state.etchPage > 0">+${{ surcharge }}</p>

            <template v-if="state.etchPage === 0">
              <slot
                v-if="
                  !(state.selectedType === 'college' || state.selectedType === 'nfl' || state.selectedType === 'nhl')
                "
                name="productOptions"
              />
              <span class="ff-text-500">Type:</span>
              <template v-for="(modifier, key) in allModifiers" :key="key">
                <CustomButton
                  v-show="isAvailableModifier(key)"
                  :class="['custom-type-option clear-btn-style pointer flex-inline', { disabled: isDisabled(key) }]"
                  :aria-label="`Select ${modifierTypes[key]?.typeLabel} etching`"
                  @click="
                    () => {
                      state.selectedType = modifierTypes[key].typeName;
                      state.etchPage++;
                      setDefaults(key);
                      $emit('type-selected');
                    }
                  "
                >
                  <div class="block etch-preview" :class="modifierTypes[key].typeName" alt="etching preview" />
                  <div class="option-data flex-col" style="margin-left: 8px">
                    <span class="ff-text-500">{{ modifierTypes[key].typeLabel }}</span>
                    <span v-if="surcharge">+{{ localeStore.currencySymbol + surcharge }}</span>
                  </div>
                </CustomButton>
              </template>
              <p class="corp-offer">
                For orders of 5 or more fire pits or corporate logos visit our
                <a href="https://offer.solostove.com/corporate-branding/" target="_blank">Corporate Sales</a>
                page.
              </p>
            </template>

            <template v-else-if="state.etchPage === 1 && state.selectedType === 'text'">
              <div>
                <span class="flex-inline ff-text-500 mgn-btm-pt5"
                  >Add Custom Text<span class="text-limit">(Max {{ state.maxLines }} Lines):</span
                  ><span class="clear pointer" @click="clearText">Clear</span></span
                >

                <Input
                  ref="customUserInput"
                  class="mgn-btm-1 etch-input"
                  name="Etch User Input"
                  :not-valid-override="state.badWordDetected || charLimit || lineLimit || invalidCharacter"
                  :input-attrs="{
                    rows: lines,
                  }"
                  :placeholder="'Type text here'"
                  variant="textarea"
                  emit-input="text-input"
                  @text-input="setAndCheckUserInput($event.value)"
                >
                  <template v-if="state.badWordDetected" #errorMessage>
                    Profane or lewd language is not permitted.
                  </template>
                  <template v-else-if="charLimit" #errorMessage>
                    Exceeded {{ state.maxTextChar }} Character Limit (Per Line)
                  </template>
                  <template v-else-if="lineLimit" #errorMessage> Exceeded {{ state.maxLines }} Line Limit </template>
                  <template v-else-if="invalidCharacter" #errorMessage> Character not currently supported. </template>
                </Input>
                <div class="etch-desc etch-disclaimer mgn-btm-1">
                  Final product may differ from the preview image.
                  <button
                    class="clear-btn-style btn-link etch-link ul pointer"
                    aria-label="Learn more about product etching"
                    @click="state.showPreviewDisclaimer = !state.showPreviewDisclaimer"
                  >
                    Learn more
                  </button>
                </div>
              </div>
            </template>

            <template v-else-if="state.etchPage === 1 && state.selectedType === 'college'">
              <div>
                <div class="disclaimer mgn-btm-1-pt5">
                  *Collegiate logos are only available on Stainless Steel fire pits.
                </div>
                <span class="flex-inline ff-text-500 mgn-btm-pt5">Add College Logo:</span>
                <select
                  v-model="selectedCollege"
                  placeholder="Select a College"
                  name="College Logo"
                  class="options-container select"
                >
                  <option
                    v-for="modifierOption in currentModifiers?.options"
                    :key="modifierOption?.option?.id"
                    :value="modifierOption"
                    class="option"
                  >
                    {{ modifierOption?.option?.label }}
                  </option>
                </select>
                <div class="etch-desc etch-disclaimer mgn-btm-1">
                  The preview image display is not an exact replica of the final product. Image size on the final
                  product may differ slightly from preview image.
                  <button
                    class="clear-btn-style etch-link ul pointer"
                    aria-label="Learn more about product etching"
                    @click="state.showPreviewDisclaimer = !state.showPreviewDisclaimer"
                  >
                    Learn more
                  </button>
                </div>
              </div>
            </template>

            <template
              v-else-if="state.etchPage === 1 && (state.selectedType === 'nfl' || state.selectedType === 'nhl')"
            >
              <div>
                <span class="flex-inline ff-text-500 mgn-btm-pt5"
                  >Add {{ state.selectedType.toUpperCase() }} Logo:</span
                >
                <select
                  v-model="selectedProTeam"
                  :placeholder="'Select a ' + state.selectedType.toUpperCase() + ' Team'"
                  :name="state.selectedType.toUpperCase() + ' Logo'"
                  class="options-container select"
                >
                  <option
                    v-for="modifierOption in currentModifiers?.options"
                    :key="modifierOption?.option?.id"
                    :value="modifierOption"
                    class="option"
                  >
                    {{ modifierOption?.option?.label }}
                  </option>
                </select>
                <div class="etch-desc etch-disclaimer mgn-btm-1">
                  The preview image display is not an exact replica of the final product. Image size on the final
                  product may differ slightly from preview image.
                  <button
                    class="clear-btn-style etch-link ul pointer"
                    aria-label="Learn more about product etching"
                    @click="state.showPreviewDisclaimer = !state.showPreviewDisclaimer"
                  >
                    Learn more
                  </button>
                </div>
              </div>
            </template>

            <div v-if="state.etchPage > 0" :class="'btm-container ' + bannerCrop('bottom')">
              <div class="mgn-btm-1 shipping-disclaimer">
                {{
                  'Please allow an additional 1-2 weeks for processing. Customized orders are FINAL SALE and can not be cancelled.'
                }}
              </div>
              <div class="etch-modal-a2c-btn">
                <div class="flex-inline">
                  <slot name="addToCart" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalWindow>
    <ModalWindow
      v-if="state.showPreviewDisclaimer"
      :unlock-body-on-close="false"
      class="disclaimer-modal"
      @closed="togglePreviewDisclaimer"
    >
      <div class="modal-body">
        <div class="h3 txt-center">Custom Text Sizing</div>
        <div class="h5">Character Sizes</div>
        <p>
          There is a fixed imprint area that is specific to each product, regardless of the number of characters chosen.
          Generally, adding more characters will result in each one being smaller.
        </p>
        <div class="h5 mgn-top-1-pt5">Imprint Area</div>
        <p>The imprint area varies based on the product.</p>
        <ul>
          <li>Bonfire: 7” W x 6” H</li>
          <li class="mgn-top-pt5">Mesa: 3.25” W x 2.35” H</li>
          <li class="mgn-top-pt5">Yukon: 7.85” W x 7.25” H</li>
          <li class="mgn-top-pt5">Ranger: 5.5” W x 5.5” H</li>
        </ul>
      </div>
    </ModalWindow>
  </div>
</template>

<script setup lang="ts">
import { ProductModifiers } from '@solo-stove/types/contentstack/stove/generated';
import { ProductVariant } from '@solo-stove/types/bigcommerce';
import { debounce } from 'lodash-es';
import { EtchingSection } from '@solo-stove/types/contentstack/stove/product';
import Filter from 'bad-words';
import Input from './Input.vue';
import { AssetCloudinary, CTSProductVariant, ModifierFull } from '~/types/contentstack';
import { getAssetPublicId } from '~/util/contentstack/csHelpers';
import { extraBadWords } from '~/util/extra-bad-words';
import { etchingSvgPattern, textSvgPattern } from '~/util/etching';
import { useDeviceStore } from '~/store/device';
import { useLocaleStore } from '~/store/locale';
// import Edit2Line from '@solo-stove/style-dictionary/output/svgs/edit-2-line.svg?component';

const localeStore = useLocaleStore();
const deviceStore = useDeviceStore();

const customUserInput = ref<InstanceType<typeof Input> | null>();
const emit = defineEmits(['type-selected', 'custom-object', 'disabled', 'close-etch-modal']);
defineExpose({
  clearText,
  toggleEtchModal,
});

const props = defineProps({
  productName: {
    type: String,
    default: '',
  },
  hasVariants: {
    type: Boolean,
    default: false,
  },
  etchingSection: {
    type: Object as () => EtchingSection,
    required: true,
  },
  selectedVariant: {
    type: Object as () => CTSProductVariant | undefined,
    default: () => undefined,
  },
  images: {
    type: Array as () => AssetCloudinary[],
    required: true,
  },
  bcVariant: {
    type: Object as () => ProductVariant,
    default: undefined,
    required: false,
  },
  bcVariants: {
    type: Array as () => ProductVariant[],
    default: () => [],
  },
  modalOpen: {
    type: Boolean,
    default: false,
  },
});

const state = reactive<{
  showEtchModal: boolean;
  etchPage: number;
  mobSizes: number[];
  userInput: string;
  firstLine: string;
  filter: Filter;
  secondLine: string;
  badWordDetected: boolean;
  selectedType: string | null;
  maxTextChar: number;
  maxLines: number;
  showPreviewDisclaimer: boolean;
  resizeKey: number;
  etchingSvg: string | undefined;
  etchingSvgPattern: string;
  textSvgPattern: string;
  baseFontSize: number;
}>({
  showEtchModal: props.modalOpen,
  etchPage: 0,
  mobSizes: [350, 400, 450, 767],
  userInput: '',
  firstLine: '',
  filter: new Filter(),
  secondLine: '',
  badWordDetected: false,
  selectedType: null,
  maxTextChar: 16,
  maxLines: 2,
  showPreviewDisclaimer: false,
  resizeKey: 1,
  etchingSvg: undefined as string | undefined,
  etchingSvgPattern,
  textSvgPattern,
  baseFontSize: 40,
});

const selectedCollege = ref<any>(null);
const selectedProTeam = ref<any>(null);
const modifierTypes = ref({
  college: {
    typeName: 'college',
    typeLabel: 'Collegiate Logos',
  },
  nfl: {
    typeName: 'nfl',
    typeLabel: 'NFL Logos',
  },
  nhl: {
    typeName: 'nhl',
    typeLabel: 'NHL Logos',
  },
  text: {
    typeName: 'text',
    typeLabel: 'Custom Text',
  },
});

interface SelectedModifier extends ProductModifiers {
  key: string;
}
const selectedVariantModifiers = computed<SelectedModifier[]>(() => {
  return (props?.selectedVariant?.enabled_modifiersConnection?.edges ?? []).map((edge) => {
    const newEdge = edge.node as SelectedModifier;
    newEdge.key = edge.node.bc_shared_modifier.name.toLowerCase().replace(/[^a-z]/g, '-');
    return newEdge;
  });
});

const surchargeModifier = computed(() => {
  return selectedVariantModifiers.value.find((node: any) => /surcharge/i.test(node?.title));
});

const surcharge = computed(() => surchargeModifier.value?.surcharge_amount);

const selectedOption = computed(() => {
  return selectedCollege.value || selectedProTeam.value;
});

const currentModifiers = computed(() => {
  return state.selectedType ? allModifiers.value[state.selectedType as keyof typeof allModifiers.value] : undefined;
});

const allModifiers = computed(() => {
  const getSharedModifier = (keyword: string | RegExp) => {
    return selectedVariantModifiers.value.find((sharedModifier) =>
      new RegExp(keyword, 'i').test(sharedModifier.bc_shared_modifier.name)
    );
  };

  const getFullModifier = (keyword: string | RegExp) => {
    return props.etchingSection?.customization?.modifiersFull?.find((node) =>
      new RegExp(keyword, 'i').test(node?.modifier?.display_name)
    );
  };

  const getSharedOption = (sharedModifier: SelectedModifier, optionLabel: string) => {
    return sharedModifier?.modifier_options?.find(
      (v) => v?.bc_shared_modifier_option?.label?.trim()?.toLowerCase() === optionLabel.trim().toLowerCase()
    );
  };

  const mapOptions = (fullModifier: ModifierFull | undefined, sharedModifier: SelectedModifier | undefined) => {
    // Currently, the only way to pair the shared modifier with the full modifier is by the option label. Thus, they must be the same.
    // We will also need to sort the array using the BC option sort_order.
    const mapped = fullModifier?.modifier?.option_values?.map((option) => {
      const shared = sharedModifier
        ? getSharedOption({ modifier_options: [], ...sharedModifier }, option?.label || '')
        : undefined;
      const logoFolder =
        sharedModifier &&
        typeof (sharedModifier as any).modifier_drive_subfolder_name === 'string' &&
        (sharedModifier as any).modifier_drive_subfolder_name.length > 0
          ? (sharedModifier as any).modifier_drive_subfolder_name
          : 'stainless';

      return {
        logo: (shared as any)?.option_imageConnection?.edges?.[0]?.node?.url,
        logoSvg: (shared as any)?.option_imageConnection?.edges?.[0]?.node?.title,
        logoFolder,
        option,
      };
    });
    return mapped?.sort((a, b) => a.option.sort_order - b.option.sort_order);
  };

  const sharedModifiers = {
    college: getSharedModifier('colleg(e|iate)'),
    nfl: getSharedModifier('nfl'),
    nhl: getSharedModifier('nhl'),
    text: getSharedModifier('text'),
  };

  const modifiersFull = {
    college: getFullModifier('colleg(e|iate)'),
    nfl: getFullModifier('nfl'),
    nhl: getFullModifier('nhl'),
    text: getFullModifier('text'),
    surcharge: getFullModifier('surcharge'),
  };

  return {
    college: {
      modifier: modifiersFull.college?.modifier?.display_name,
      options: mapOptions(modifiersFull.college, sharedModifiers.college),
      modifierId: null,
      surcharge: modifiersFull.college?.choice,
    },
    nfl: {
      modifier: modifiersFull.nfl?.modifier?.display_name,
      options: mapOptions(modifiersFull.nfl, sharedModifiers.nfl),
      modifierId: null,
      surcharge: modifiersFull.nfl?.choice,
    },
    nhl: {
      modifier: modifiersFull.nhl?.modifier?.display_name,
      options: mapOptions(modifiersFull.nhl, sharedModifiers.nhl),
      modifierId: null,
      surcharge: modifiersFull.nhl?.choice,
    },
    text: {
      modifier: modifiersFull.text?.modifier?.display_name,
      options: [],
      modifierId: modifiersFull.text?.modifier?.id,
      surcharge: modifiersFull.text?.choice,
    },
  };
});

const productModifiers = computed(() => {
  const currentType = state.selectedType as keyof typeof allModifiers.value;
  const modifierType = currentType ? modifierTypes.value[currentType] : undefined;
  const data = currentType === 'text' ? state.userInput : selectedOption.value?.option?.label;
  const modifierOptionId =
    currentType === 'text' ? currentModifiers.value?.modifierId : selectedOption.value?.option?.option_id;
  const modifierJson = data
    ? JSON.stringify({
        type: currentType,
        data,
        // logoSvg: selectedOption.value?.logoSvg,
        logoFolder: selectedOption.value?.logoFolder,
        layoutHeight: etchHeight.value,
        layoutWidth: etchWidth.value,
      })
    : undefined;

  const modifier = {
    typeName: modifierType?.typeName,
    typeLabel: modifierType?.typeLabel,
    modifier: {
      option_id: modifierOptionId,
      option_value: currentType === 'text' ? modifierJson : selectedOption.value?.option?.id,
      name: currentModifiers.value?.modifier,
      value: modifierJson,
      valueId: 0,
    },
    surcharge: {
      option_id: currentModifiers.value?.surcharge?.option_values?.[0]?.option_id,
      option_value: currentModifiers.value?.surcharge?.option_values?.[0]?.id,
      name: currentModifiers.value?.surcharge?.display_name,
      value: currentModifiers.value?.surcharge?.option_values?.[0]?.adjusters?.price?.adjuster_value,
      valueId: 0,
    },
  };
  return modifier;
});

const ctaText = computed(() => {
  let text = '';
  const ctaArr = [];
  const enabledModifierTypes = (props.selectedVariant?.enabled_modifiersConnection?.edges || []).map((edge) => {
    const name = edge.node.bc_shared_modifier?.name;
    if (!name) return null;
    if (/nfl/i.test(name)) return 'nfl';
    if (/nhl/i.test(name)) return 'nhl';
    if (/colleg(e|iate)/i.test(name)) return 'college';
    if (/text/i.test(name)) return 'text';
    return null;
  });
  if (enabledModifierTypes.includes('nfl')) ctaArr.push('NFL Logos');
  if (enabledModifierTypes.includes('nhl')) ctaArr.push('NHL Logos');
  if (enabledModifierTypes.includes('college')) ctaArr.push('College Logos');
  if (enabledModifierTypes.includes('text')) ctaArr.push('Custom Text');
  if (enabledModifierTypes.length > 0) text += 'Personalize Yours with ';
  if (ctaArr.length > 2) {
    text += ctaArr.slice(0, -1).join(', ') + ', or ' + ctaArr.slice(-1);
  } else if (ctaArr.length === 2) {
    text += ctaArr.join(' or ');
  } else {
    text += ctaArr[0];
  }
  return enabledModifierTypes.length > 0 ? text : null;
});

const cloudinaryId = computed<string>(() => {
  return props.hasVariants
    ? getAssetPublicId(props.images)
    : getAssetPublicId(props?.etchingSection?.preview_image as any[]);
});

const inputRows = computed<string[]>(() => state.userInput?.split('\n'));
const charLimit = computed<boolean>(() => {
  let maxedChars = false;
  inputRows.value?.forEach((row: string) => {
    if (row.length > state.maxTextChar) maxedChars = true;
  });
  return maxedChars;
});
const lineLimit = computed<boolean>(() => inputRows.value?.length > state.maxLines);
const invalidCharacter = computed<boolean>(() => {
  const regexTester = /^[A-Za-z0-9!@#$%^&*()<>,./?[\]{}|\\\n-_=+]{0,}$/;
  const result = !regexTester.test(state.userInput);
  return result;
});
const lines = computed<any>(() => state.userInput?.split('\n').length);
const prodWidth = computed<number>(() => props?.etchingSection?.dimensions?.product_width || 0);
const prodHeight = computed<number>(() => props?.etchingSection?.dimensions?.product_height || 0);
const etchWidth = computed<number>(() => props?.etchingSection?.dimensions?.etch_area_width || 0);
const etchHeight = computed<number>(() => props?.etchingSection?.dimensions?.etch_area_height || 0);
const styleEtchingDimensions = computed<string>(() => {
  const widthPercent = (etchWidth.value / prodWidth.value) * 100;
  const heightPercent = (etchHeight.value / prodHeight.value) * 100;
  return `width: ${widthPercent}%; height: ${heightPercent}%;`;
});

const imageOptions = computed(() => {
  const dimensions: ImageOptions = {
    width: undefined,
    height: undefined,
    // gravity: 'center',
  };
  let ratioAdjustment = 1;
  if (prodHeight.value > prodWidth.value) ratioAdjustment = prodWidth.value / prodHeight.value;
  dimensions.width = deviceStore.isMobile
    ? 227 * ratioAdjustment
    : deviceStore.isDesktop
    ? 325 * ratioAdjustment
    : 410 * ratioAdjustment;
  dimensions.height = deviceStore.isMobile ? 227 : deviceStore.isDesktop ? 325 : 410;
  dimensions.quality = deviceStore.isMobile || deviceStore.isTablet ? 'auto:eco' : 'auto';
  return dimensions as ImageOptions;
});

const etchingColor = computed(
  () => props?.selectedVariant?.etching_color || props?.etchingSection?.etching_color || '#655F5D'
);

// Only for College NFL, and NHL options
watch([selectedOption], async () => {
  emit('custom-object', {
    type: state.selectedType,
    data: productModifiers.value,
  });
  emit('disabled', false);
  let etchingSvg = await getEtchingSvg();
  etchingSvg = etchingSvg
    ?.replace(/\n/gi, '')
    .replace('<?xml version="1.0" encoding="UTF-8"?>', '')
    .replace(/<defs>.*?<\/defs>/gi, '');
  state.etchingSvg = etchingSvg;
});

onMounted(() => {
  state.filter?.addWords(...extraBadWords);
  window.addEventListener('resize', debouncedResizeHandler);
});
onUnmounted(() => {
  window.removeEventListener('resize', debouncedResizeHandler);
});

const resizeCalculations = () => {
  state.resizeKey++;
  if (state.resizeKey > 15) state.resizeKey = 1;
};

const debouncedResizeHandler = debounce(resizeCalculations, 500);
function setDefaults(key: string) {
  if (key === 'college') {
    selectedCollege.value = allModifiers.value?.college?.options?.[0];
  } else if (key === 'nfl') {
    selectedProTeam.value = allModifiers.value?.nfl?.options?.[0];
  } else if (key === 'nhl') {
    selectedProTeam.value = allModifiers.value?.nhl?.options?.[0];
  }
}
function backButton(): void {
  clearText();
  state.selectedType = null;
  selectedCollege.value = null;
  selectedProTeam.value = null;
  state.etchPage--;
  emit('type-selected');
}
function toggleEtchModal(): void {
  state.showEtchModal = !state.showEtchModal;
  if (!state.showEtchModal) {
    resetEtchExperience();
    emit('close-etch-modal');
  }
}

function bannerCrop(loc: string) {
  const overlayBanner = document.getElementsByClassName('top-banner')[0] as HTMLElement;
  let usedClass = '';
  if (overlayBanner !== undefined) {
    if (loc === 'top') {
      usedClass = 'modal-etch-padded';
    } else if (loc === 'bottom') {
      usedClass = 'btm-adjust-padded';
    }
  }
  return usedClass;
}
function togglePreviewDisclaimer(): void {
  state.showPreviewDisclaimer = !state.showPreviewDisclaimer;
}
function resetEtchExperience() {
  clearText();
  state.etchPage = 0;
  selectedProTeam.value = null;
  selectedCollege.value = null;
  state.selectedType = null;
}
function setAndCheckUserInput(input: string): void {
  state.userInput = input;
  state.badWordDetected = checkForBadWords();
  const isDisabled =
    state.badWordDetected ||
    charLimit.value ||
    lineLimit.value ||
    state.userInput?.length === 0 ||
    invalidCharacter.value;

  emit('disabled', isDisabled);
  if (!isDisabled) {
    emit('custom-object', {
      type: state.selectedType,
      data: productModifiers.value,
    });
  }
}
function checkForBadWords(): boolean {
  const profane = state.filter?.isProfane(state.userInput);
  return profane;
}
function clearText(): void {
  state.userInput = '';
  setAndCheckUserInput('');
  (customUserInput.value as any)?.clearInput();
}
async function getEtchingSvg(): Promise<string | undefined> {
  try {
    const url = selectedCollege.value
      ? selectedCollege.value?.logo
      : selectedProTeam.value
      ? selectedProTeam.value?.logo
      : undefined;
    if (!url) return Promise.resolve(undefined);

    // cache or no-cache?
    const res = await $fetch<string | undefined>(url, {
      parseResponse: (txt) => txt,
    });
    return res;
  } catch (error) {
    console.warn(error); // eslint-disable-line no-console
    return Promise.resolve(undefined);
  }
}
function isDisabled(key: string): boolean {
  if (key.includes('text')) {
    return false;
  }
  return !selectedVariantModifiers.value.find((node) => node.key.includes(key));
}
function isAvailableModifier(key: keyof typeof allModifiers.value): boolean {
  const currModifierObj = allModifiers.value[key];
  return (
    currModifierObj !== undefined &&
    currModifierObj.modifier !== undefined &&
    currModifierObj.options !== undefined &&
    currModifierObj.surcharge !== undefined
  );
}
</script>

<style lang="scss" scoped>
:deep(.modal-etch-padded) {
  @include local-mixins.desktop {
    padding-top: #{local-functions.rem-calc(40)};
    height: auto;
  }
}
.etching-section {
  .img-container {
    :deep(.current-image) {
      display: flex;
      width: 100%;
      height: auto;
      @include local-mixins.tablet_and_mobile {
        width: auto;
        height: 30vh;
        max-height: 58.25vw;
      }
      @include local-mixins.desktop {
        max-width: 740px;
      }
    }
  }
}
</style>
<style lang="scss">
.etching-section {
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  .modal-window-container.modal-etch.full-window {
    @include local-mixins.tablet_and_mobile {
      .modal-content {
        min-height: unset;
      }
      .modal-window-close {
        background-color: $color-neutral-white;
      }
    }
  }
  .right-arrow svg {
    fill: $color-primary-500;
    margin-right: 0.75rem;
  }
  .customize-section {
    padding: 1rem 1rem 0 1rem;
  }
  .etch-preview {
    border-radius: 4px;
    &.nfl {
      @include local-mixins.backgroundImage(local-functions.svg-img-source('nfl-etch-preview.png'), 4rem, 4rem);
    }
    &.nhl {
      @include local-mixins.backgroundImage(local-functions.svg-img-source('nhl-etch-preview.png'), 4rem, 4rem);
    }
    &.college {
      @include local-mixins.backgroundImage(local-functions.svg-img-source('college-etch-preview.png'), 4rem, 4rem);
    }
    &.text {
      @include local-mixins.backgroundImage(local-functions.svg-img-source('text-etch-preview.png'), 4rem, 4rem);
    }
    // has to come after to override bgImg style
    &.college,
    &.text,
    &.nfl,
    &.nhl {
      min-width: 4rem;
      min-height: 4rem;
      background-size: cover;
    }
  }
  .img-container {
    position: relative;
    width: 100%;
    height: auto;
    background-color: $color-neutral-cool-50;
    display: flex;
    justify-content: center;
    align-items: center;

    .etch-img-overlay-container {
      position: relative;
      @include local-mixins.tablet_and_mobile {
        margin: 1rem 0;
      }
    }

    .etch-img-overlay {
      font-size: 2rem;
      font-weight: 500;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 17%;

      &--text {
        left: 50%;
        transform: translateX(-50%);
      }

      &--svg {
        left: 50%;
        transform: translateX(-50%);
      }

      img {
        margin: 0 auto;
        width: 40%;
        height: auto;
      }

      span {
        width: 100%;
        text-align: center;
      }

      svg {
        opacity: 0.6;
        max-height: -webkit-fill-available;
        height: 100%;
        width: 100%;
      }

      path,
      circle,
      ellipse,
      line,
      polygon,
      polyline,
      rect {
        fill: v-bind(etchingColor);
      }
      text {
        fill: url(#bg-svg-text);
      }
      &.img {
        svg {
          max-height: 70%;
        }
      }
    }
  }
  .etch-img-pattern,
  .etch-txt-pattern {
    position: absolute;
    z-index: -1;
  }
  .etch-img-pattern {
    background-color: var(--bg-color-etching);
  }
  .user-input {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 36px;
    color: $color-neutral-cool-400;
    display: inline-block;
    white-space: nowrap;
    text-align: center;
    width: 370px;
    font-weight: 500;
  }
  .etch-input {
    .solo-input {
      min-height: revert;
      height: revert;
      line-height: 1.2;
      padding: revert;
    }
  }
  .custom-type-option {
    width: 100%;
    padding: 1rem;
    margin-top: 1rem;
    border: 1px solid $color-neutral-cool-200;
    border-radius: 4px;
    justify-content: flex-start;
    &.disabled {
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.5;
    }
    &:hover,
    &:focus {
      border-color: $color-neutral-cool-900;
      background-color: inherit;
    }
  }
  .corp-offer {
    font-size: 0.875rem;
    margin-top: 1.5rem;
  }
  .flex-inline.cta:not(.etch-desc) {
    gap: 12px;
    padding: 0.75rem;
    cursor: pointer;
    border-radius: 4px;
    background-color: $color-neutral-cool-50;
    .svg-comp-wrapper {
      margin-left: auto;
      transition: transform 0.2s ease;
    }
    &:hover {
      .svg-comp-wrapper {
        transform: translateX(8px);
      }
    }
  }

  .etch-header {
    font-weight: 500;
    font-size: 0.875rem;
  }
  .header-logo {
    @include local-mixins.backgroundImage(local-functions.svg-img-source('header-logo.svg'), 0.875rem, 3.875rem);
    margin-left: 2px;
    margin-bottom: 3px;
  }
  .preview-img {
    border-radius: 3px;
    width: 4rem;
    height: 3.75rem;
  }
  .etch-desc {
    font-size: 0.8125rem;
    &.cta {
      font-size: 0.625rem;
      line-height: 0.875rem;
    }

    &.etch-disclaimer {
      line-height: 1.5;
    }
  }
  .etch-link {
    font-size: 0.875rem;
    &.cta {
      margin: auto 0;
      margin-left: auto;
    }
  }
  .option-data {
    gap: 0.375rem;
    text-align: left;
  }
  .clear {
    color: $color-neutral-cool-600;
    font-size: 0.875rem;
    font-weight: normal;
    margin-left: auto;
  }
  .text-limit {
    font-weight: normal;
    margin-left: 5px;
  }
  .back {
    color: $color-primary-500;
    margin-bottom: 0;
    svg {
      margin-right: 0.75rem;
      fill: $color-primary-500;
    }
    @include local-mixins.desktop {
      margin-top: -1.5rem;
    }
  }
  .disclaimer {
    font-size: 0.75rem;
    font-style: italic;
  }
  .modal-body {
    width: 100%;
  }
  .edit {
    width: 39px;
    height: 18px;
    margin-right: 10px;
  }
  .btm-container {
    position: sticky;
    bottom: 0;
    background-color: $color-neutral-white;
    border-top: solid 1px $color-neutral-cool-300;
    margin: auto -2rem 0 -2rem;
    padding: 1rem 2rem 1.5rem 2rem;
    @include local-mixins.tablet_and_mobile {
      margin: auto -1rem 0 -1rem;
      padding: 1rem 1rem 1.5rem 1rem;
    }
  }
  .btm-adjust-padded {
    @include local-mixins.desktop {
      padding: 1rem 2rem 4rem 2rem;
    }
  }
  .stepper-comp-wrapper,
  .back-button {
    margin-right: 1rem;
  }
  .shipping-disclaimer {
    line-height: 1.5;
  }
  .disclaimer-modal {
    .modal-body {
      padding: 1rem;
      display: flex;
      flex-direction: column;
    }
    .h3 {
      padding-top: 3rem;
      padding-bottom: 1rem;
      margin-bottom: 1rem;
      border-bottom: 1px solid $color-neutral-cool-200;
    }
    .img-placeholder {
      width: 100px;
      height: 100px;
      border-radius: 4px;
      background-color: #d9d9d9;
    }
    .images {
      column-gap: 1rem;
    }
    .modal-content {
      border-radius: 8px;
    }
    ul {
      list-style: disc;
      list-style-type: disc;
      margin-left: 1rem;
    }

    @include local-mixins.desktop {
      .modal-content {
        max-width: 450px;
      }
    }
  }
  @include local-mixins.tablet_and_mobile {
    .img-container {
      height: auto;
    }
    .modal-body-content,
    .customize-section {
      height: 100%;
      min-height: 80vh;
    }
    .shipping-disclaimer {
      font-size: 0.875rem;
    }
  }
  @include local-mixins.desktop {
    .img-container {
      width: 58%;
      height: 100%;
      min-height: 100vh;
    }
    .customize-section {
      height: 100vh;
      min-height: 80vh;
      overflow-y: scroll;
      width: 42%;
      padding: 3rem 2rem 0 2rem;
    }
  }
}
</style>
